import React, { useEffect, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useThemeMode } from '../context/ThemeContext';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import '../index.css'; // Ensure global styles are loaded
import './Navbar.css'; // Import custom styles for animation and color
import MenuIcon from '@mui/icons-material/Menu';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';

const Navbar = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { mode, toggleMode } = useThemeMode();
    const [scrolled, setScrolled] = useState(false);
    const [showBackToTop, setShowBackToTop] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const logoSrc = isMobile
        ? (mode === 'dark'
            ? '/simplitech_logo_transparent.svg'
            : '/simplitech_logo_transparent_black_border.svg')
        : (mode === 'dark'
            ? '/simplitech_with_white_logo_transparent.svg'
            : '/simplitech_with_black_logo_transparent.svg');

    // Colors based on mode (optional, can use theme.palette if desired)
    const iconColor = mode === 'dark' ? '#fff' : '#222';

    // Track scroll position
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
            setShowBackToTop(window.scrollY > 100);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Glassmorphic background style
    const glassBg = mode === 'dark'
        ? 'rgba(30, 30, 30, 0.7)'
        : 'rgba(255, 255, 255, 0.7)';
    const appBarStyle = scrolled
        ? {
            background: glassBg,
            backdropFilter: 'blur(12px)',
            boxShadow: '0 4px 24px 0 rgba(0,0,0,0.08)',
            transition: 'background 0.3s, box-shadow 0.3s',
        }
        : {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            transition: 'background 0.3s, box-shadow 0.3s',
        };

    // Handler for back to top
    const handleBackToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const navLinks = [
        { label: 'Home', to: '/' },
        { label: 'About', to: '/about' },
        { label: 'Services', to: '/services' },
        { label: 'Contact Us', to: '/contact' },
    ];

    return (
        <Box sx={{ borderRadius: 4 }}>
            <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
            <AppBar
                position="fixed"
                color="transparent"
                elevation={0}
                sx={appBarStyle}
                className={`navbar-animated${scrolled ? ' navbar-scrolled' : ''}`}
            >
                <Toolbar sx={{ position: 'relative', minHeight: { xs: 56, sm: 64 } }}>
                    {/* Left: Logo */}
                    <Box sx={{ display: 'flex', alignItems: 'center', zIndex: 2 }}>
                        <RouterLink to="/" style={{ display: 'flex', alignItems: 'center' }}>
                            <img
                                src={logoSrc}
                                alt="SimpliTechs Logo"
                                className="navbar-logo-animated"
                                style={{ height: isMobile ? 32 : 48, marginRight: 12, cursor: 'pointer' }}
                            />
                        </RouterLink>
                    </Box>
                    {/* Center: Links (desktop only) */}
                    {!isMobile && (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '50%',
                                top: 0,
                                transform: 'translateX(-50%)',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                zIndex: 1,
                            }}
                        >
                            {navLinks.map((link) => (
                                <Button
                                    key={link.to}
                                    color="inherit"
                                    component={RouterLink}
                                    to={link.to}
                                    className="navbar-link-animated"
                                >
                                    {link.label}
                                </Button>
                            ))}
                        </Box>
                    )}
                    {/* Right: Dark/Light Mode Icon and Burger Menu */}
                    <Box sx={{ marginLeft: 'auto', zIndex: 2, display: 'flex', alignItems: 'center' }}>
                        <IconButton onClick={toggleMode} sx={{ color: iconColor }} className="navbar-mode-animated">
                            {mode === 'dark' ? <DarkModeIcon /> : <LightModeIcon />}
                        </IconButton>
                        {isMobile && (
                            <IconButton
                                edge="end"
                                color="inherit"
                                aria-label="menu"
                                onClick={() => setDrawerOpen(true)}
                                sx={{ ml: 1 }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>
            {/* Drawer for mobile nav */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{
                    sx: {
                        width: window.innerWidth < 600 ? '80vw' : 320,
                        maxWidth: '100vw',
                        background: glassBg,
                        backdropFilter: 'blur(12px)',
                        boxSizing: 'border-box',
                        overflowX: 'hidden',
                    }
                }}
            >
                <List sx={{ mt: 2, width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
                    {navLinks.map((link) => (
                        <ListItem key={link.to} disablePadding>
                            <ListItemButton
                                component={RouterLink}
                                to={link.to}
                                onClick={() => setDrawerOpen(false)}
                            >
                                <ListItemText
                                    primary={link.label}
                                    primaryTypographyProps={{ className: 'navbar-link-animated', sx: { fontSize: 18, fontWeight: 600 } }}
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            </Drawer>
            {/* Back to Top Button */}
            {showBackToTop && (
                <IconButton
                    onClick={handleBackToTop}
                    className="navbar-backtotop-animated"
                    sx={{
                        position: 'fixed',
                        bottom: 32,
                        right: 32,
                        zIndex: 1300,
                        background: theme.palette.background.paper,
                        color: iconColor,
                        boxShadow: '0 4px 16px 0 rgba(0,0,0,0.18)',
                        border: `2px solid ${theme.palette.primary.main}`,
                        '&:hover': {
                            background: theme.palette.background.default,
                        },
                        transition: 'background 0.2s, box-shadow 0.2s',
                    }}
                    size="large"
                    aria-label="Back to top"
                >
                    <KeyboardArrowUpIcon fontSize="inherit" />
                </IconButton>
            )}
        </Box>
    );
};

export default Navbar; 